import { z } from 'zod';
import { REEL_TYPES, type ReelType } from '../kinds.js';
import { passportFormatsSchema } from '../schemas/content.js';

/*
 * The contract with the engine (spec 5.2): a mirror of
 * faceless_reels/engine/contract/*.schema.json, engine 0.11.0, order_version 2
 * (version 1 is still accepted: snapshots of builds queued before the update).
 * The order is strict like the engine's schema (an extra field is refused as
 * order_invalid); what the engine writes back is read leniently, so a new field
 * in a later engine does not break the bot.
 */

export const ENGINE_REFUSAL_CODES = [
  'order_invalid',
  'style_unknown',
  'language_unsupported',
  'too_many_blocks',
  'block_too_long',
  'slide_text_too_long',
  'reel_too_long',
  'audio_unreadable',
  'own_voice_quality',
  'voice_mismatch',
  'provider_unavailable',
  'timeout',
  'internal_error',
  'type_unknown',
  'style_not_for_type',
] as const;
export type EngineRefusalCode = (typeof ENGINE_REFUSAL_CODES)[number];

/** The person must change the text (spec 5.2: «Экран «Текст» на указанном блоке»). */
export const TEXT_REFUSALS = ['too_many_blocks', 'block_too_long', 'slide_text_too_long', 'reel_too_long'] as const;
/** Our mistake, not the person's (spec 5.2, decision 30). */
export const BOT_REFUSALS = [
  'order_invalid',
  'style_unknown',
  'language_unsupported',
  'type_unknown',
  'style_not_for_type',
] as const;

const BLOCK_ID = /^[A-Za-z0-9_-]{1,32}$/;
const HEX = /^#[0-9A-Fa-f]{6}$/;

export const engineBlockSchema = z
  .object({
    id: z.string().regex(BLOCK_ID),
    role: z.enum(['hook', 'body', 'cta']),
    text: z.string().min(1),
    title: z.string().min(1).optional(),
    spoken: z.string().min(1).optional(),
  })
  .strict();
export type EngineBlock = z.infer<typeof engineBlockSchema>;

/**
 * How many body phrases a type allows, where the engine counts phrases by role
 * (contract 0.7.0: «Подборка» — hook 1–2, body 3–21, cta 0–1, else
 * order_invalid). The passport does not carry these counts, so they live here.
 */
export const BODY_BLOCKS: Partial<Record<ReelType, readonly [number, number]>> = { listicle: [3, 21] };

/** order.json (order.schema.json) plus the rules the engine's README adds to it. */
export const engineOrderSchema = z
  .object({
    order_version: z.union([z.literal(1), z.literal(2)]),
    /** Version 2 only, and always there; version 1 is «Сцены». */
    type: z.enum(REEL_TYPES).optional(),
    order_id: z.string().min(1).max(100),
    format: z.enum(['reel', 'carousel']),
    animated: z.boolean(),
    style: z.string().min(1).max(40),
    accent: z.string().regex(HEX).optional(),
    language: z.string().min(2).max(8),
    author: z.object({ handle: z.string().min(1).max(60), name: z.string().max(100) }).strict(),
    blocks: z.array(engineBlockSchema).min(1),
    /**
     * `checked` (engine 0.11.0): the bot has checked the take and given it to the person to
     * listen to, so the engine warns about a mismatch instead of refusing (2026-10-05).
     */
    voice: z
      .object({ file: z.string().min(1), source: z.enum(['clone', 'own']), checked: z.boolean().optional() })
      .strict()
      .optional(),
    music: z.enum(['auto', 'none']).optional(),
    watermark: z.boolean().optional(),
  })
  .strict()
  .superRefine((o, ctx) => {
    const bad = (message: string) => ctx.addIssue({ code: z.ZodIssueCode.custom, message });
    const reel = o.format === 'reel';
    if (o.order_version === 2 && o.type === undefined) bad('type: required in order_version 2');
    if (o.order_version === 1 && o.type !== undefined) bad('type: only in order_version 2');
    if (o.type !== undefined && o.type !== 'scenes' && !reel) bad(`type ${o.type}: a reel only`);
    if (reel && !o.animated) bad('a reel is always animated');
    if (reel !== (o.voice !== undefined)) bad('voice: only and always for a reel');
    if (o.watermark) bad('watermark: false until the open launch');
    const last = o.blocks.length - 1;
    o.blocks.forEach((b, i) => {
      if (b.role === 'hook' && i !== 0) bad(`${b.id}: hook is not first`);
      if (b.role === 'cta' && i !== last) bad(`${b.id}: cta is not last`);
      if (reel && b.title !== undefined) bad(`${b.id}: title is for a slide only`);
      if (!reel && b.spoken !== undefined) bad(`${b.id}: spoken is for a reel only`);
      if (reel && ![b.text, b.spoken ?? 'x'].every((s) => /[\p{L}\p{N}]/u.test(s))) {
        bad(`${b.id}: no words to voice`);
      }
    });
    if (new Set(o.blocks.map((b) => b.id)).size !== o.blocks.length) bad('block ids repeat');
    const body = o.type ? BODY_BLOCKS[o.type] : undefined;
    const bodies = o.blocks.filter((b) => b.role === 'body').length;
    if (body && (bodies < body[0] || bodies > body[1])) bad(`type ${o.type}: ${body[0]}–${body[1]} body blocks`);
  });
export type EngineOrder = z.infer<typeof engineOrderSchema>;

const engineCostSchema = z.object({ llm: z.number().min(0), stt: z.number().min(0), images: z.number().min(0) });

/**
 * A refusal. A code of a later engine is read as internal_error, the original
 * kept in `unknown_code` (the bot's field, for the owner), so parsing never fails on it.
 */
export const engineRefusalSchema = z.preprocess(
  (raw) => {
    if (raw === null || typeof raw !== 'object') return raw;
    const code = (raw as { code?: unknown }).code;
    if (typeof code !== 'string' || (ENGINE_REFUSAL_CODES as readonly string[]).includes(code)) return raw;
    return { ...raw, code: 'internal_error', unknown_code: code };
  },
  z.object({
    status: z.literal('refused'),
    order_id: z.string().optional(),
    code: z.enum(ENGINE_REFUSAL_CODES),
    unknown_code: z.string().optional(),
    stage: z.string(),
    message: z.string().min(1),
    retryable: z.boolean(),
    block: z.string().optional(),
    /** 0.4.0+: what the command spent before it refused. */
    cost_usd: engineCostSchema.optional(),
  }),
);
export type EngineRefusal = z.infer<typeof engineRefusalSchema>;

export const engineFileSchema = z.object({
  kind: z.enum(['video', 'image', 'archive']),
  path: z.string().min(1),
  width: z.number().int().optional(),
  height: z.number().int().optional(),
  duration_ms: z.number().int().optional(),
  size_bytes: z.number().int().min(0),
});
export type EngineFile = z.infer<typeof engineFileSchema>;

/** out/result.json of `render` when it is done. */
export const engineResultOkSchema = z.object({
  status: z.literal('ok'),
  order_id: z.string(),
  format: z.enum(['reel', 'carousel']),
  /** 0.5.0+; absent in the stub's and older results. */
  type: z.string().optional(),
  style: z.string(),
  files: z.array(engineFileSchema).min(1),
  cover: z.string().min(1),
  cost_usd: engineCostSchema,
  versions: z.object({ engine: z.string(), library: z.string(), plan_cached: z.boolean() }),
  warnings: z.array(z.string()),
});
export type EngineResultOk = z.infer<typeof engineResultOkSchema>;
export const engineResultSchema = z.union([engineResultOkSchema, engineRefusalSchema]);

export const engineAlignIssueSchema = z.object({
  type: z.enum(['missing', 'extra', 'substituted']),
  expected: z.string().optional(),
  heard: z.string().optional(),
  at_ms: z.number().int(),
});

/** out/align.json of `align` when it could listen. */
export const engineAlignOkSchema = z.object({
  status: z.literal('ok'),
  order_id: z.string().optional(),
  duration_ms: z.number().int().min(0),
  blocks: z.array(
    z.object({
      id: z.string(),
      start_ms: z.number().int(),
      end_ms: z.number().int(),
      matched: z.number().min(0).max(1),
      issues: z.array(engineAlignIssueSchema),
    }),
  ),
  words: z.array(z.unknown()),
  verdict: z.enum(['ok', 'mismatch']),
  cost_usd: z.object({ stt: z.number().min(0) }),
  warnings: z.array(z.string()),
});
export type EngineAlignOk = z.infer<typeof engineAlignOkSchema>;
export const engineAlignSchema = z.union([engineAlignOkSchema, engineRefusalSchema]);

/** out/progress.json while `align` or `render` works. */
export const engineProgressSchema = z.object({
  command: z.enum(['align', 'render']),
  stage: z.string().min(1),
  done: z.number().int().min(0),
  total: z.number().int().min(1),
  updated_at: z.string(),
});
export type EngineProgress = z.infer<typeof engineProgressSchema>;

const stylePreviewSchema = z
  .object({ image: z.string().nullable().default(null), video: z.string().nullable().default(null) })
  .passthrough();

/** A style (theme). Fields of a later engine are kept: the worker stores the body as read. */
export const engineStyleSchema = z
  .object({
    id: z.string().min(1),
    name: z.string(),
    about: z.string(),
    accent_default: z.string().regex(HEX),
    /** The «Сцены» picture (540×960); null for a theme of «Схема» only. */
    preview_image: z.string().nullable(),
    /** The «Сцены» demo reel with sound (540×960). */
    preview_video: z.string().nullable(),
    /** 0.4.0+: the cover of a carousel (540×675). */
    preview_carousel: z.string().nullable().optional(),
    /** 0.5.0+: the types this theme draws. */
    types: z.array(z.string()).optional(),
    /** 0.5.0+: whom it suits (spec 7.3). */
    niches: z.array(z.string()).optional(),
    /** 0.5.0+: picture and demo by type. */
    previews: z.record(stylePreviewSchema).optional(),
  })
  .passthrough();
export type EngineStyle = z.infer<typeof engineStyleSchema>;

/** `types[]` of passport.json (0.5.0+); read leniently — passportTypes() checks the limits. */
export const engineTypeSchema = z
  .object({
    id: z.string().min(1),
    name: z.string(),
    about: z.string(),
    formats: z.record(z.unknown()),
    styles: z.array(z.string()),
  })
  .passthrough();

/** passport.json. The body is stored as the engine wrote it; this reads what the bot uses. */
export const enginePassportSchema = z
  .object({
    engine_version: z.string().min(1),
    library_version: z.string().min(1),
    languages: z.array(z.string()).min(1),
    /** Engine ≤ 0.4.0 reads only these; 0.5.0+ repeats «Сцены» here and has `types`. */
    formats: passportFormatsSchema.optional(),
    types: z.array(engineTypeSchema).optional(),
    styles: z.array(engineStyleSchema).min(1),
    scenes: z.array(z.object({ id: z.string(), name: z.string(), good_for: z.string() }).passthrough()),
    music: z.array(z.enum(['auto', 'none'])),
  })
  .passthrough();
export type EnginePassport = z.infer<typeof enginePassportSchema>;
